import { CreateReportSchedule, ReportScheduleItem, DispatchedReportItem } from '@futrli-sdk/report_scheduling';
import { API_PATHS, interpolatePathParams, REQUEST_METHODS, ROUTE_PARAMS } from '@futrli/shared-services';

import { makeRequestWithResponse } from '../shared';
import { QueryParams } from '../../types';

export const createScheduleReport = async (
  workspaceId: string,
  organisationId: string,
  data: Partial<CreateReportSchedule>,
  consolidation = false
): Promise<CreateReportSchedule> => {
  const path = consolidation ? API_PATHS.REPORT_SCHEDULE_CONSOLIDATION : API_PATHS.REPORT_SCHEDULE;
  const url = interpolatePathParams(path, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<CreateReportSchedule>({ url, method: REQUEST_METHODS.POST, data });
};

export const updateScheduleReport = async (
  workspaceId: string,
  organisationId: string,
  scheduleReportId: string,
  data: Partial<CreateReportSchedule>,
  consolidation = false
): Promise<CreateReportSchedule> => {
  const path = consolidation ? API_PATHS.REPORT_SCHEDULE_BY_ID_CONSOLIDATION : API_PATHS.REPORT_SCHEDULE_BY_ID;
  const url = interpolatePathParams(path, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.SCHEDULE_REPORT_ID]: scheduleReportId
  });

  return makeRequestWithResponse<CreateReportSchedule>({ url, method: REQUEST_METHODS.PATCH, data });
};

export const getScheduledReports = async (
  workspaceId: string,
  organisationId: string,
  queryParams: QueryParams = {},
  consolidation = false
): Promise<ReportScheduleItem[]> => {
  const path = consolidation ? API_PATHS.REPORT_SCHEDULE_CONSOLIDATION : API_PATHS.REPORT_SCHEDULE;
  const url = interpolatePathParams(path, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse({ url, method: REQUEST_METHODS.GET, params: queryParams });
};

export const getDispatchedReports = async (
  workspaceId: string,
  organisationId: string,
  queryParams: QueryParams = {},
  consolidation = false
): Promise<DispatchedReportItem[]> => {
  const path = consolidation ? API_PATHS.REPORT_DISPATCHED_CONSOLIDATION : API_PATHS.REPORT_DISPATCHED;
  const url = interpolatePathParams(path, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse({ url, method: REQUEST_METHODS.GET, params: queryParams });
};

export const getScheduleReportDetails = async (
  workspaceId: string,
  organisationId: string,
  scheduleReportId: string,
  consolidation = false
): Promise<ReportScheduleItem> => {
  const path = consolidation ? API_PATHS.REPORT_SCHEDULE_BY_ID_CONSOLIDATION : API_PATHS.REPORT_SCHEDULE_BY_ID;
  const url = interpolatePathParams(path, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.SCHEDULE_REPORT_ID]: scheduleReportId
  });

  return makeRequestWithResponse({ url, method: REQUEST_METHODS.GET });
};

export const deleteScheduledReport = async (
  workspaceId: string,
  organisationId: string,
  scheduleReportId: string,
  consolidation = false
): Promise<ReportScheduleItem[]> => {
  const path = consolidation ? API_PATHS.REPORT_SCHEDULE_BY_ID_CONSOLIDATION : API_PATHS.REPORT_SCHEDULE_BY_ID;
  const url = interpolatePathParams(path, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.SCHEDULE_REPORT_ID]: scheduleReportId
  });

  return makeRequestWithResponse({ url, method: REQUEST_METHODS.DELETE });
};

export const deleteDispatchedReport = async (
  workspaceId: string,
  organisationId: string,
  dispatchedReportId: string,
  consolidation = false
): Promise<void> => {
  const path = consolidation ? API_PATHS.REPORT_DISPATCHED_BY_ID_CONSOLIDATION : API_PATHS.REPORT_DISPATCHED_BY_ID;
  const url = interpolatePathParams(path, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.DISPATCHED_REPORT_ID]: dispatchedReportId
  });

  return makeRequestWithResponse({ url, method: REQUEST_METHODS.DELETE });
};

export const approveDispatchedReport = async (
  workspaceId: string,
  organisationId: string,
  dispatchedReportId: string,
  consolidation = false
): Promise<void> => {
  const path = consolidation ? API_PATHS.APPROVE_REPORT_DISPATCHED_CONSOLIDATION : API_PATHS.APPROVE_REPORT_DISPATCHED;
  const url = interpolatePathParams(path, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.DISPATCHED_REPORT_ID]: dispatchedReportId
  });

  return makeRequestWithResponse<void>({ url, method: REQUEST_METHODS.PUT });
};

export const denyDispatchedReport = async (
  workspaceId: string,
  organisationId: string,
  dispatchedReportId: string,
  consolidation = false
): Promise<void> => {
  const path = consolidation ? API_PATHS.DENY_REPORT_DISPATCHED_CONSOLIDATION : API_PATHS.DENY_REPORT_DISPATCHED;
  const url = interpolatePathParams(path, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.DISPATCHED_REPORT_ID]: dispatchedReportId
  });

  return makeRequestWithResponse<void>({ url, method: REQUEST_METHODS.PUT });
};
